import { createSelector } from '@reduxjs/toolkit';
import { getLeafNodes } from 'modules/Editor/store/infographSlice.helpers';
import { generateBackgroundString } from 'modules/common/components/Page/Page.helpers';
import { PageId, WidgetId } from 'types/idTypes';
import {
  Color,
  InfographState,
  Language,
  PageNumber,
  PageSize,
  PageToWidgetsMap,
  ShowOnPage,
} from 'types/infographTypes';
import { BackgroundPattern, Gradient, PageBackgroundData } from 'types/pageTypes';
import { TreeNode } from 'types/structuredContentTypes';
import { AccessibleElement, Widget, WidgetType } from 'types/widget.types';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { StatChartType } from 'widgets/ResponsiveWidgets/StatChartWidget/StatChartWidget.types';
import { getPageWidgetIds, getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, GroupWidgetData } from 'widgets/Widget.types';
import { DEFAULT_PAGE_NUMBER_STYLE } from 'widgets/sdk/PageNumber/PageNumber.config';

import { MAXIMUM_PAGE_LIMIT } from '../Editor.config';
import { findFontsInPage } from './infographSlice.helpers';
import { RootState } from './store';

const selectInfographId = (state: RootState) => state.infograph.id;
const selectInfograph = (state: RootState): InfographState => state.infograph;
const selectPageOrder = (state: RootState): PageId[] => state.infograph.pageOrder;
const selectPhishing = (state: RootState): boolean => state.infograph.phishing?.isPhishing || false;
const selectPage = (pageId: PageId) => (state: RootState) => state.infograph.pages[pageId];
const selectWidget = (widgetId: WidgetId) => (state: RootState) => state.infograph.widgets[widgetId];

/**
 * Selects the group widget that contains the given member widget ID.
 *
 * @param {WidgetId} memberWidgetId - The ID of the member widget we are searching the group for.
 * @returns - A function that takes the state and returns a tuple:
 * - The first element is the data of the group widget that contains the member widget, or null if not found.
 * - The second element is the ID of the group widget, or an empty string if not found.
 */
const selectGroupWidgetFromMemberWidgetId =
  (memberWidgetId: WidgetId) =>
  (state: RootState): [AllWidgetData | Widget | null, string] => {
    // Iterate through all widgets in the state to find the group widget that contains the memberWidgetId
    for (const [widgetId, widgetData] of Object.entries(state.infograph.widgets)) {
      // Check if the widget is a group widget and if it contains the memberWidgetId
      if (
        getWidgetTypeFromId(widgetId) === WidgetType.Group &&
        'memberWidgetIds' in widgetData &&
        widgetData.memberWidgetIds.includes(memberWidgetId)
      ) {
        // Return the group widget and its ID
        return [state.infograph.widgets[widgetId], widgetId];
      }
    }
    // If no group widget is found that contains the memberWidgetId, return null and an empty string
    return [null, ''];
  };

const selectGroupWidgets = createSelector(
  (state: RootState) => state.infograph.widgets,
  (allWidgets) => {
    return Object.entries(allWidgets)
      .filter(
        ([widgetId, widgetData]) =>
          getWidgetTypeFromId(widgetId) === WidgetType.Group &&
          'memberWidgetIds' in widgetData &&
          widgetData.memberWidgetIds.length >= 2,
      )
      .map(([widgetId, widgetData]) => ({ widgetId, widgetData: widgetData as GroupWidgetData }));
  },
);

const selectAltText = (widgetId: WidgetId) => (state: RootState) =>
  (state.infograph.widgets[widgetId] as AccessibleElement)?.altText;
const selectIsDecorative = (widgetId: WidgetId) => (state: RootState) =>
  (state.infograph.widgets[widgetId] as AccessibleElement)?.isDecorative;
const selectWidgets = (state: RootState) => state.infograph.widgets;
const selectInfographWidthPx = (state: RootState): number => state.infograph.size.widthPx;
const selectInfographHeightPx = (state: RootState): number => state.infograph.size.heightPx;
const selectInfographSize = (state: RootState): PageSize => state.infograph.size;
const selectInfographTitle = (state: RootState): string => state.infograph.title;
const selectLanguage = (state: RootState): Language => state.infograph.language;
const selectColorSwatch = (state: RootState): Color[] => state.infograph.colorSwatch;
const selectHasReachedPageLimit = (state: RootState): boolean => state.infograph.pageOrder.length >= MAXIMUM_PAGE_LIMIT;
// How many pages the infograph has
const selectPageCount = (state: RootState): number => state.infograph.pageOrder.length;
// Select the entire page number setting
const selectPageNumber = (state: RootState): PageNumber | undefined => state.infograph.pageNumber;
// If the infograph has page numbers
const selectHasPageNumber = (state: RootState): boolean => state.infograph.pageNumber?.isEnabled ?? false;
// What page number to start displaying page numbers at
const selectPageNumberStart = (state: RootState): number => state.infograph.pageNumber?.start ?? 1;
// The setting for what pages to show page numbers on
const selectPageNumberShowOnPages = (state: RootState): ShowOnPage => state.infograph.pageNumber?.showOnPages ?? 'all';
const selectPageNumberCustomRange = (state: RootState): number[] => state.infograph.pageNumber?.range ?? [];
// The custom page range as an object for easier lookup to see if a page number is included in the custom range
// (ex: { 1: true, 2: true, 3: true })
const selectPageNumberCustomRangeAsObject = createSelector(selectInfograph, (state: InfographState) => {
  const range = state.pageNumber?.range ?? [];
  return range.reduce((acc, pageNumber) => {
    acc[pageNumber] = true;
    return acc;
  }, {} as { [key: number]: boolean });
});

// Select the style of the page number
const selectPageNumberStyle = (state: RootState) => state.infograph.pageNumber?.style ?? DEFAULT_PAGE_NUMBER_STYLE;

const selectIsWidgetLocked = (state: RootState) => {
  const activeWidgets = state.widgetControl.activeWidgets;
  if (activeWidgets.length === 0) return false;

  // if more than one widget is selected, return true if all are locked
  if (activeWidgets.length > 1) {
    return activeWidgets.every((widget) => !!state.infograph.widgets[widget.id]?.isLocked);
  }
  const widgetId = activeWidgets?.[0]?.id;
  return !!state.infograph.widgets[widgetId]?.isLocked;
};

const selectIsWidgetLockDisabled = (state: RootState) => {
  const activeWidgetIds = state.widgetControl.activeWidgetIds;
  if (activeWidgetIds.length === 0) return true;
  // check if activeWidgets are already locked (grouped widgets also have more than one active widget)
  let shouldDisable: boolean;
  shouldDisable = activeWidgetIds.some((widgetId) => {
    const widget = selectWidget(widgetId)(state);
    if (!widget) return false;
    if ('type' in widget && !!widget?.type) {
      const widgetType = widget.type as StatChartType;
      // disable locking if any of the widgets is responsive group type (stat charts)
      return Object.values(StatChartType).includes(widgetType);
    }
    return false;
  });
  return shouldDisable;
};

const selectWidgetLayerOrder = (activePageId: PageId) =>
  createSelector(selectInfograph, (infograph: InfographState): WidgetId[] => {
    if (!activePageId) {
      return [];
    }
    return infograph.pages[activePageId].widgetLayerOrder;
  });

const selectWidgetProperty =
  <WidgetData extends AllWidgetData & Widget, Key extends keyof WidgetData>(widgetId: WidgetId, property: Key) =>
  (state: RootState): WidgetData[Key] | undefined =>
    (state.infograph.widgets[widgetId] as WidgetData)?.[property];

const selectPageToWidgetMap = createSelector(selectInfograph, (state: InfographState) => {
  const pageToWidgetMap: PageToWidgetsMap = {};
  const pages = state.pages;
  const pageOrder = state.pageOrder;

  for (const pageId of pageOrder) {
    const page = pages[pageId];
    const allPageWidgetIds = getPageWidgetIds(page, state.widgets);

    if (allPageWidgetIds && allPageWidgetIds.length > 0) {
      pageToWidgetMap[pageId] = allPageWidgetIds.map((widgetId) => ({
        widgetId,
        widgetData: state.widgets[widgetId],
      }));
    }
  }

  return pageToWidgetMap;
});

/**
 * Select a pageId to widget data map - widgets are ordered by reading order
 * using the accessibility tree.
 */
const selectPageToWidgetMapByReadingOrder = createSelector(selectInfograph, (state: InfographState) => {
  const pageToWidgetMap: PageToWidgetsMap = {};
  const pages = state.pages;
  const pageOrder = state.pageOrder;
  const widgets = state.widgets;

  for (const pageId of pageOrder) {
    const { widgetStructureTree } = pages[pageId];
    const widgetIds = widgetStructureTree ? getLeafNodes(widgetStructureTree as TreeNode) : [];

    if (widgetIds && widgetIds.length > 0) {
      pageToWidgetMap[pageId] = widgetIds.map((widgetId) => ({
        widgetId,
        widgetData: widgets[widgetId],
      }));
    }
  }

  return pageToWidgetMap;
});

const selectPageBackground =
  (pageId: PageId) =>
  (state: RootState): string => {
    if (pageId && state.infograph.pages[pageId]) return state.infograph.pages[pageId].background;
    return '';
  };

const selectGradientBackground =
  (pageId: PageId) =>
  (state: RootState): Gradient | null | undefined => {
    if (pageId && state.infograph.pages[pageId]) return state.infograph.pages[pageId].gradientBackground;
    return null;
  };

const selectBackgroundPattern =
  (pageId: PageId) =>
  (state: RootState): BackgroundPattern | undefined => {
    if (pageId && state.infograph.pages[pageId]) return state.infograph.pages[pageId].backgroundPattern;
  };
/**
 * Selector to get all data related to rendering the page background
 * (i.e.) solid color, gradient background, pattern, etc.
 *
 * @param pageId
 * @returns an object - { background: string, gradientBackground: Gradient | null, backgroundPattern: BackgroundPattern | undefined }
 */
const selectAllPageBackgroundData = (pageId: PageId) =>
  createSelector(selectInfograph, (infograph: InfographState): PageBackgroundData => {
    if (!pageId || !infograph.pages[pageId]) return { background: '', gradientBackground: null };

    return {
      background: infograph.pages[pageId].background,
      gradientBackground: infograph.pages[pageId].gradientBackground,
      backgroundPattern: infograph.pages[pageId].backgroundPattern,
    };
  });

const selectPageFonts = (pageId: PageId) =>
  // TODO: See if we can do findFonts somewhere else
  createSelector(selectInfograph, (infograph: InfographState): string[] => {
    return findFontsInPage(infograph, pageId);
  });

const selectPageStructureTree =
  (pageId: PageId) =>
  (state: RootState): TreeNode | undefined => {
    if (pageId && state.infograph.pages[pageId]) return state.infograph.pages[pageId].widgetStructureTree as TreeNode;
  };

/**
 * Select page background string to be used in the page background style
 * If `pageId` is not defined, will default to the current active page
 *
 * @param pageId page background to return
 * @returns page background string
 */
const selectPageBackgroundString =
  (pageId?: PageId) =>
  (state: RootState): string => {
    const activePageId = state.pageControl.activePageId;
    const page = state.infograph.pages[pageId ?? activePageId];
    if (!page) return '';

    return generateBackgroundString({
      background: page.background,
      gradientBackground: page.gradientBackground,
      backgroundPattern: page.backgroundPattern,
    });
  };

// selector for checking if the infograph has loaded
const selectHasInfographLoaded = (state: RootState): boolean => !!state.pageControl.activePageId;

const selectHasActiveWidgetBackgroundRemoved = (state: RootState): boolean => {
  const activeWidgets = state.widgetControl.activeWidgets;
  if (activeWidgets.length !== 1) return false;

  const widget = activeWidgets[0];
  const widgetType = getWidgetTypeFromId(activeWidgets[0].id);
  if (widgetType !== WidgetType.Image) return false;

  const widgetData = state.infograph.widgets[widget.id] as ImageWidgetData;

  return !!widgetData?.filter?.backgroundRemoval?.isBackgroundRemoved;
};

const selectHasFormWidgets = (state: RootState): boolean => {
  if (!state.infograph?.widgets) return false;

  return Object.keys(state.infograph.widgets).some((widgetId) => {
    return getWidgetTypeFromId(widgetId) === WidgetType.Form;
  });
};

export {
  selectInfographId,
  selectInfograph,
  selectHasInfographLoaded,
  selectPageOrder,
  selectPage,
  selectWidget,
  selectWidgets,
  selectIsWidgetLocked,
  selectIsWidgetLockDisabled,
  selectAltText,
  selectIsDecorative,
  selectInfographWidthPx,
  selectInfographHeightPx,
  selectInfographSize,
  selectInfographTitle,
  selectPageBackground,
  selectBackgroundPattern,
  selectPageFonts,
  selectLanguage,
  selectPageStructureTree,
  selectPageToWidgetMap,
  selectColorSwatch,
  selectPageToWidgetMapByReadingOrder,
  selectHasReachedPageLimit,
  selectWidgetProperty,
  selectWidgetLayerOrder,
  selectHasPageNumber,
  selectGroupWidgets,
  selectPageNumberStart,
  selectPageNumberShowOnPages,
  selectPageCount,
  selectPageNumberCustomRange,
  selectPageNumberCustomRangeAsObject,
  selectPageNumber,
  selectPageNumberStyle,
  selectGradientBackground,
  selectAllPageBackgroundData,
  selectGroupWidgetFromMemberWidgetId,
  selectPageBackgroundString,
  selectHasActiveWidgetBackgroundRemoved,
  selectPhishing,
  selectHasFormWidgets,
};
